import React, { createContext, useContext } from 'react'
import cn from 'classnames'
import InfiniteScroll from 'react-infinite-scroll-component'
import { PLPGrid } from '@corratech/pylot-plp-grid'
import {
    LoaderTile,
    ProductItem
} from '@components/common/ProductTile/ProductTilePlp'
import { WishlistProvider } from '@lib/context/WishlistContext'
import { PLPCartProvider } from '@lib/context/PLPCartContext'
import { useLowStockBadges } from '@lib/hooks/product/low-stock/useLowStockBadge'
import { useEnrichSearchConfigurables } from '@pylot-data/hooks/search/use-enrich-search-configurables'
import { PLP_CONFIG } from '@pylot/constants/plp'
import type { ProductInterface, CategoryTree, Maybe } from '@pylot/pylotschema'
import s from '@pagestyles/Plp.module.scss'

const PAGE_TYPES = {
    PLP: 'plp',
    SEARCH: 'search'
} as const

interface ProductGridProps {
    productsToRender: Maybe<ProductInterface>[]
    marketingTiles?: any[]
    category?: Maybe<CategoryTree> | undefined
    shouldDisplayLoading: boolean
    gridColumns: 4 | 7
    isLast: boolean
    onLoadMore: () => void
    LoadingIndicator: React.ComponentType
    pageType?: 'plp' | 'search'
    pageSize?: number
    scrollThreshold?: number
    className?: string
    /** La última carga de página falló: se ofrece un botón para reintentar. */
    hasError?: boolean
    onRetry?: () => void
    retryLabel?: string
}

// Context to provide shared props to ProductItemWrapper without changing PLPGrid interface
const ProductGridContext = createContext<{
    category?: Maybe<CategoryTree>
    pageType: 'plp' | 'search'
    gridColumns: 4 | 7
    lowStockBySku: Record<string, boolean>
    lowStockBadgeLabel: string
}>({
    pageType: 'plp',
    gridColumns: 4,
    lowStockBySku: {},
    lowStockBadgeLabel: ''
})

// Create a wrapper for the ProductItem to handle both PLP and search contexts
const ProductItemWrapper: React.FC<{
    product: ProductInterface
}> = ({ product }) => {
    const {
        category,
        pageType,
        gridColumns,
        lowStockBySku,
        lowStockBadgeLabel
    } = useContext(ProductGridContext)

    const enableVideo =
        gridColumns === 4
            ? PLP_CONFIG.ENABLE_VIDEO_GRID_4
            : gridColumns === 7
              ? PLP_CONFIG.ENABLE_VIDEO_GRID_7
              : false

    const lowStockBadgeVisible = Boolean(
        product.sku &&
        product.stock_status !== 'OUT_OF_STOCK' &&
        lowStockBySku[product.sku] === true
    )

    return (
        <ProductItem
            product={product}
            imageParam={{ width: 500 }}
            category={category}
            lowStockBadgeVisible={lowStockBadgeVisible}
            lowStockBadgeLabel={lowStockBadgeLabel}
            enableVideo={enableVideo}
        />
    )
}

export const ProductGrid: React.FC<ProductGridProps> = ({
    productsToRender,
    marketingTiles = [],
    category,
    shouldDisplayLoading,
    gridColumns,
    isLast,
    onLoadMore,
    LoadingIndicator,
    pageType = 'plp',
    pageSize = PLP_CONFIG.PAGE_SIZE,
    scrollThreshold = PLP_CONFIG.SCROLL_THRESHOLD,
    className,
    hasError = false,
    onRetry,
    retryLabel
}) => {
    // Filter out null values before passing to PLPGrid
    const filteredProducts = productsToRender.filter(
        (product): product is ProductInterface =>
            product !== null && product !== undefined
    )
    const validateSearchProductTypes = pageType === PAGE_TYPES.SEARCH
    // Live Search solo devuelve seis campos por item: se completa el producto de
    // Magento en lote, igual que el payload de la PLP. De aqui salen las options
    // del "agregar rapido" y, desde EVO-263, `associated_products`.
    const productsForGrid = useEnrichSearchConfigurables(
        filteredProducts,
        validateSearchProductTypes
    )
    const { lowStockBySku, label: lowStockBadgeLabel } = useLowStockBadges(
        productsForGrid,
        true,
        validateSearchProductTypes
    )

    // react-infinite-scroll-component solo vuelve a llamar a `next` cuando cambia
    // `dataLength`; si la petición falló, el usuario necesita un reintento manual.
    const showRetry = !isLast && hasError && !shouldDisplayLoading && !!onRetry

    return (
        <InfiniteScroll
            dataLength={productsForGrid.length}
            next={onLoadMore}
            hasMore={!isLast && !hasError}
            loader={<LoadingIndicator />}
            scrollThreshold={scrollThreshold}
        >
            <div className={`${s['plp-grid-wrap']} ${className || ''}`}>
                <ProductGridContext.Provider
                    value={{
                        category,
                        pageType,
                        gridColumns,
                        lowStockBySku,
                        lowStockBadgeLabel
                    }}
                >
                    <PLPCartProvider>
                        <WishlistProvider>
                            <PLPGrid
                                marketingTiles={marketingTiles}
                                ProductTile={ProductItemWrapper as any}
                                LoadingTile={LoaderTile}
                                products={productsForGrid}
                                isLoading={shouldDisplayLoading}
                                pageSize={pageSize}
                                plpColumns={gridColumns}
                            />
                        </WishlistProvider>
                    </PLPCartProvider>
                </ProductGridContext.Provider>
            </div>
            {showRetry && (
                <div className={s['load-more-retry']}>
                    <button
                        type="button"
                        className={cn(
                            'btn btn-secondary',
                            s['load-more-retry-button']
                        )}
                        data-testid="plp-load-more-retry"
                        onClick={onRetry}
                    >
                        {retryLabel ?? 'Ver más'}
                    </button>
                </div>
            )}
        </InfiniteScroll>
    )
}

// Export specialized versions for convenience
export const PLPProductGrid: React.FC<Omit<ProductGridProps, 'pageType'>> = (
    props
) => <ProductGrid {...props} pageType={PAGE_TYPES.PLP} />

export const SearchProductGrid: React.FC<
    Omit<ProductGridProps, 'pageType' | 'marketingTiles' | 'category'> & {
        marketingTiles?: any[]
        category?: Maybe<CategoryTree> | undefined
    }
> = (props) => <ProductGrid {...props} pageType={PAGE_TYPES.SEARCH} />
